import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * The Bricks mobile menu drawer.
 *
 * Deliberately kept separate from `header-drawer.js` so the stock drawer keeps
 * working and the two can be swapped from theme settings.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element wrapping the drawer.
 * @property {HTMLDivElement} panel - The sliding panel.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawerBricks extends Component {
  requiredRefs = ['details', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the drawer when the Escape key is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
  };

  /**
   * @returns {boolean} Whether the drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle the drawer.
   *
   * The native summary toggle is suppressed so that closing can wait for the
   * slide-out animation instead of the panel vanishing on the spot.
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the drawer.
   */
  open() {
    const { details, panel } = this.refs;
    const summary = details.querySelector('summary');

    summary?.setAttribute('aria-expanded', 'true');
    details.classList.remove('menu-closing');
    details.setAttribute('open', '');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      onAnimationEnd(panel, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Close the drawer, waiting for the slide-out animation before removing the
   * `open` attribute so the panel does not disappear mid-animation.
   */
  close() {
    const { details, panel } = this.refs;

    if (!this.isOpen) return;

    const summary = details.querySelector('summary');

    summary?.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.classList.add('menu-closing');

    onAnimationEnd(
      panel,
      () => {
        details.classList.remove('menu-closing');
        details.removeAttribute('open');
        removeTrapFocus();
      },
      { subtree: false }
    );
  }
}

if (!customElements.get('header-drawer-bricks')) {
  customElements.define('header-drawer-bricks', HeaderDrawerBricks);
}
